import React from 'react';
import { Button, Drawer, Row, Table, Tag } from 'antd';
import { useState, useEffect } from 'react';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { fetchPhysicianItems, applySorting } from 'services/settings/lookup_table/physician';
import PhysicianForm from './physicianForm.jsx';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import DragableBodyRow from 'shared_components/drag_and_drop_row';
import { showMessage } from 'services/settings/message';

function Physician() {
	const [physician, setPhysician] = useState([]);
	const [hasReordered, setHasReordered] = useState(false);
	const [drawerState, setDrawerState] = useState({
		visible: false,
		drawerTitle: '',
		selecetedData: null,
		buttonNames: '',
	});

	useEffect(() => {
		getPhysicians();
	}, []);

	const getPhysicians = async () => {
		const data = await fetchPhysicianItems();
		// Sort by physicianSort ASC, but put records without physicianSort at the end
		const sortedData = (data || []).sort((a, b) => {
			if (a.physicianSort != null && b.physicianSort != null) {
				return a.physicianSort - b.physicianSort;
			}
			if (a.physicianSort == null) return 1;
			if (b.physicianSort == null) return -1;
			return 0;
		});
		setPhysician(sortedData);
	};

	const onSuccessCreate = async () => {
		onDrawerClose();
		await getPhysicians();
		showMessage('M0002');
	};

	const onSuccessUpdate = async () => {
		onDrawerClose();
		await getPhysicians();
		showMessage('M0003');
	};

	const applySort = async () => {
		const dataPayload = physician.map((rec, index) => {
			return {
				physicianID: rec.physicianID,
				physicianSort: index + 1,
			};
		});

		await applySorting(dataPayload);
		setHasReordered(false);
	};

	const moveRow = (dragIndex, hoverIndex) => {
		console.log('=== DRAG AND DROP DEBUG ===');
		console.log('dragIndex:', dragIndex);
		console.log('hoverIndex:', hoverIndex);
		console.log('Current physician array length:', physician.length);
		console.log('Current physician array:', physician);

		const dragRow = physician[dragIndex];
		console.log('Dragged row:', dragRow);

		const updatedData = update({ physician }, {
			physician: { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] }
		});

		console.log('Updated data:', updatedData);
		console.log('Updated physician array length:', updatedData.physician.length);
		console.log('Updated physician array:', updatedData.physician);
		console.log('=== END DEBUG ===');

		setPhysician(updatedData.physician);
		setHasReordered(true);
	};

	const showDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'ADD PHYSICIAN',
			selecetedData: null,
			buttonNames: 'ADD',
		});
	};

	const displayDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'UPDATE PHYSICIAN',
			selecetedData: record,
			buttonNames: 'UPDATE',
		});
	};

	const onDrawerClose = () => {
		setDrawerState({
			visible: false,
			drawerTitle: '',
			selecetedData: null,
			buttonNames: '',
		});
	};

	const components = {
		body: {
			row: DragableBodyRow,
		},
	};

	const columns = [
		{
			title: 'PREFIX',
			dataIndex: 'physicianPrefix',
			width: 80,
		},
		{
			title: 'FIRST NAME',
			dataIndex: 'physicianFirstName',
			width: 150,
			sorter: (a, b) => { return a.physicianFirstName.localeCompare(b.physicianFirstName) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'MIDDLE NAME',
			dataIndex: 'physicianMiddleName',
			width: 150,
		},
		{
			title: 'LAST NAME',
			dataIndex: 'physicianLastName',
			width: 150,
			sorter: (a, b) => { return a.physicianLastName.localeCompare(b.physicianLastName) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'SUFFIX',
			dataIndex: 'physicianSuffix',
			width: 80,
		},
		{
			title: 'DEGREE',
			dataIndex: 'physicianDegree',
			width: 100,
		},
		{
			title: 'PHYSICIAN LICENSE NO',
			dataIndex: 'physicianLicenseNo',
			width: 200,
		},
		{
			title: 'VALIDITY',
			dataIndex: 'physicianValidity',
			width: 150,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'DATE CREATED',
			dataIndex: 'createdDate',
			width: 150,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'STATUS',
			dataIndex: 'isActive',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? 'red' : 'green'}>{active === 0 ? 'INACTIVE' : 'ACTIVE'}</Tag>
			),
		},
	];

	return (
		<>
			<Row justify="end" style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: '10px' }}>
				<DownloadCSV
					confirmRequired={true}
					isLookupTable={true}
					confirmDetails={{
						section: 'PHYSICIANS',
						count: physician.length
					}}
					data={physician}
					filename="physicians"
					columns={[
						{ title: 'PREFIX', dataIndex: 'physicianPrefix' },
						{ title: 'FIRST NAME', dataIndex: 'physicianFirstName' },
						{ title: 'MIDDLE NAME', dataIndex: 'physicianMiddleName' },
						{ title: 'LAST NAME', dataIndex: 'physicianLastName' },
						{ title: 'SUFFIX', dataIndex: 'physicianSuffix' },
						{ title: 'DEGREE', dataIndex: 'physicianDegree' },
						{ title: 'PHYSICIAN LICENSE NO', dataIndex: 'physicianLicenseNo' },
						{ 
							title: 'VALIDITY', 
							dataIndex: 'physicianValidity',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'DATE CREATED', 
							dataIndex: 'createdDate',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'STATUS', 
							dataIndex: 'isActive',
							render: active => active === 0 ? 'INACTIVE' : 'ACTIVE'
						}
					]}
				/>
				<Button onClick={applySort} shape="round" type="primary" disabled={!hasReordered}>
					APPLY SORTING
				</Button>
				<Button
					type="primary"
					shape="round"
					onClick={showDrawer}
					style={{ marginRight: '15px' }}
				>
					ADD PHYSICIAN
				</Button>
			</Row>
			<DndProvider backend={HTML5Backend}>
				<Table
					dataSource={physician}
					columns={columns}
					components={components}
					pagination={false}
					rowKey={record => record.physicianID}
					onRow={(record, index) => ({
						index: physician.findIndex(item => item.physicianID === record.physicianID),
						moveRow: moveRow,
						onDoubleClick: () => {
							displayDrawer(record);
						},
					})}
				/>
			</DndProvider>
			<Drawer
				title={drawerState.drawerTitle}
				width="30%"
				visible={drawerState.visible}
				onClose={onDrawerClose}
				destroyOnClose
			>
				<PhysicianForm
					buttonNames={drawerState.buttonNames}
					onDrawerClose={onDrawerClose}
					selecetedData={drawerState.selecetedData}
					onSuccessCreate={onSuccessCreate}
					onSuccessUpdate={onSuccessUpdate}
					maxPhysicianSort={Math.max(...physician.map(p => p.physicianSort || 0), 0)}
				/>
			</Drawer>
		</>
	);
}

export default Physician;
